import { useSetAtom } from "jotai";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import clsx from "clsx";
import { MetricInfoDisplay } from "./MetricInfoDisplay";
import { useMemo } from "react";
import { MetricLineage } from "@/apps/AdminBetaApp/sections/companies/MetricsLab/MetricLineage";
import dayjs from "dayjs";
import { Metric } from "@/entities/Finance/Finance.model";
import { AIMContextViewAtom } from "../state/context.state";

function formatPeriodForLineage(period: string): string {
    const quarterMatch = period.match(/(\d{4})-Q(\d)/);
    if (quarterMatch) {
        const year = quarterMatch[1];
        const quarter = quarterMatch[2];
        return `Q${quarter}-${year}`;
    }
    if (period.includes("<")) {
        return period
            .split(/<\d+>/)
            .map((date) => date.split("-").reverse().join("-"))
            .join(" - ");
    }
    const monthMatch = period.match(/^(\d{4})-(\d{2})$/);
    if (monthMatch) {
        const year = monthMatch[1];
        const month = monthMatch[2];
        return `${month}-${year}`;
    }
    const date = dayjs(period);
    if (date.isValid()) {
        return date.format("MM-YYYY");
    }
    return period;
}

interface EmptyStateCardProps {
    heading: string;
    body: string;
}

function EmptyStateCard({ heading, body }: EmptyStateCardProps) {
    return (
        <div
            className={clsx("p-4", "flex flex-col", "gap-4 rounded")}
            style={{ backgroundColor: "#2E3849" }}
        >
            <h3 className="heading-s">{heading}</h3>
            <p className="body-s">{body}</p>
        </div>
    );
}

export function LineagePanel({
    metric,
    period,
    title,
}: {
    metric: Metric | null;
    period: string;
    title: string;
}) {
    console.log(metric, period, title);
    const setAIMContextView = useSetAtom(AIMContextViewAtom);

    const parsedMetric = useMemo(() => {
        if (!metric?.lineagedValues) {
            return null;
        }

        const lineagedValues = metric.lineagedValues as any[];

        if (lineagedValues.length === 0) {
            return null;
        }

        const matchedPeriod = lineagedValues.find((pm) => pm.period === period);

        return matchedPeriod || null;
    }, [metric, period]);

    if (!metric) {
        return null;
    }

    const hasLineageData =
        metric?.lineagedValues &&
        Array.isArray(metric.lineagedValues) &&
        metric.lineagedValues.length > 0;

    return (
        <div
            className={clsx(
                "px-6",
                "custom-scrollbar absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-4 flex flex-col">
                <RightSidebarButton
                    onClick={() => {
                        setAIMContextView(null);
                    }}
                />
                {!hasLineageData && (
                    <span
                        className={clsx(
                            "ui-label",
                            "mb-2 w-fit rounded-full px-3 py-1",
                        )}
                        style={{
                            backgroundColor: "#E2E8F0",
                            color: "#0F172A",
                        }}
                    >
                        Coming soon
                    </span>
                )}
                <h2 className="pageheading ">
                    {title}
                    {parsedMetric?.period
                        ? ` — ${formatPeriodForLineage(parsedMetric.period)}`
                        : period
                          ? ` — ${formatPeriodForLineage(period)}`
                          : ""}
                </h2>
            </div>
            <div className="custom-scrollbar hide-scrollbar overflow-y-scroll">
                <div className="flex flex-col gap-4">
                    {metric && <MetricInfoDisplay metric={metric} />}

                    {hasLineageData && parsedMetric ? (
                        <MetricLineage metric={parsedMetric} />
                    ) : !hasLineageData ? (
                        <EmptyStateCard
                            heading="The lineage view feature is not supported in the current version."
                            body="We're still working on this, check back soon for the full experience!"
                        />
                    ) : (
                        <EmptyStateCard
                            heading="Data for this field is missing or unavailable."
                            body="Please contact your admin or company representative for more information"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
